import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, PauseCircle, Loader2, Mail, Plus, Power, Send, Trash2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { ForwardingGuide } from "./ForwardingGuide";

// قاعدة عامة لكل ما يُطلب من العميل: أحمر = مطلوب/غير مكتمل، أخضر = مكتمل، برتقالي = متوقف
export const TONE = {
  todo: { ring: "border-red-500/40 bg-red-500/5", icon: "text-red-500", dot: "bg-red-500", Icon: AlertCircle },
  done: { ring: "border-emerald-500/30 bg-emerald-500/5", icon: "text-emerald-500", dot: "bg-emerald-500", Icon: CheckCircle2 },
  paused: { ring: "border-orange-500/40 bg-orange-500/5", icon: "text-orange-500", dot: "bg-orange-500", Icon: PauseCircle },
};
export const mailboxState = (mb) => (mb.enabled === false ? "paused" : mb.forwarding_verified ? "done" : "todo");
const LABEL = { done: "نشط", todo: "مطلوب إكمال الإعداد", paused: "متوقف مؤقتًا" };

export const MailboxSwitcher = ({ boxes, selected, onSelect, onAdd, isAdmin }) => (
  <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1" data-testid="mail-switcher">
    {boxes.map((b) => {
      const t = TONE[mailboxState(b)];
      return (
        <button key={b.id} onClick={() => onSelect(b.id)} title={LABEL[mailboxState(b)]} dir="ltr" className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-bold flex items-center gap-2 transition-colors ${selected === b.id ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground hover:text-foreground"}`} data-testid={`mail-switch-${b.id}`}>
          <span className={`w-2 h-2 rounded-full ${t.dot}`} /><Mail className="w-3.5 h-3.5" /> {b.address}
        </button>
      );
    })}
    {isAdmin && <button onClick={onAdd} className="shrink-0 rounded-full border border-dashed border-border px-3 py-1.5 text-xs font-bold text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="mail-switch-add"><Plus className="w-3.5 h-3.5" /> إضافة بريد</button>}
  </div>
);

const Step = ({ tone, n, title, children, testId }) => {
  const t = TONE[tone];
  return (
    <div className={`flex gap-3 rounded-2xl border p-4 ${t.ring}`} data-testid={testId} data-tone={tone}>
      <t.Icon className={`w-6 h-6 shrink-0 ${t.icon}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold flex items-center gap-2"><span className={`w-5 h-5 rounded-full text-[11px] grid place-items-center text-white ${t.dot}`}>{n}</span>{title}</div>
        <div className="mt-2 space-y-2">{children}</div>
      </div>
    </div>
  );
};

const CopyField = ({ value, testId }) => {
  const [c, setC] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard?.writeText(value); setC(true); setTimeout(() => setC(false), 1500); }} className="w-full flex items-center justify-between gap-2 rounded-xl bg-background border border-border px-3 py-2 text-sm font-mono hover:border-primary/50" dir="ltr" data-testid={testId}>
      <span className="truncate">{value}</span>{c ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
    </button>
  );
};

// البطاقة الوحيدة التي يراها العميل: كل المطلوب منه بالتسلسل وبالألوان
export const MailboxLifecycle = ({ mb, onChange, isAdmin }) => {
  const [busy, setBusy] = useState("");
  const [waiting, setWaiting] = useState(false);
  const state = mailboxState(mb);
  const t = TONE[state];
  useEffect(() => {
    if (!waiting) return undefined;
    const iv = setInterval(async () => {
      try { const r = await api.get(`/mail/mailboxes/${mb.id}`); if (r.data.forwarding_verified) { setWaiting(false); toast.success("التوجيه يعمل ✓ — الصندوق نشط"); onChange(); } } catch { /* ignore */ }
    }, 5000);
    const stop = setTimeout(() => { setWaiting(false); toast.error("لم تعد رسالة التحقق خلال 3 دقائق — راجع إعداد التوجيه ثم أعد المحاولة"); }, 180000);
    return () => { clearInterval(iv); clearTimeout(stop); };
  }, [waiting, mb.id, onChange]);
  const verify = async () => {
    setBusy("v");
    try { await api.post(`/mail/mailboxes/${mb.id}/verify-forwarding`); setWaiting(true); toast.info("أُرسلت رسالة التحقق إلى بريدك — ننتظر عودتها عبر التوجيه (حتى 3 دقائق)"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); } finally { setBusy(""); }
  };
  const toggle = async (v) => {
    setBusy("t");
    try { await api.patch(`/mail/mailboxes/${mb.id}`, { enabled: v }); toast.success(v ? "تم تفعيل الرد التلقائي" : "تم إيقاف الرد التلقائي"); onChange(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); } finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm(`إزالة الصندوق ${mb.address}؟ تبقى المراسلات محفوظة ويتوقف الرد.`)) return;
    try { await api.delete(`/mail/mailboxes/${mb.id}`); toast.success("أُزيل الصندوق"); onChange(true); } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
  };
  const fwdTone = mb.forwarding_verified ? "done" : "todo";
  return (
    <div className="space-y-3" data-testid="mail-lifecycle">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} /><span className="font-bold text-sm" data-testid="mail-state-label">{LABEL[state]}</span><span className="text-xs text-muted-foreground" dir="ltr">{mb.address}</span></div>
        {<button onClick={remove} className="text-xs text-red-500 hover:underline flex items-center gap-1" data-testid="mail-delete-btn"><Trash2 className="w-3.5 h-3.5" /> إزالة</button>}
      </div>

      <Step tone="done" n="1" title="بريدك مُسجّل" testId="mail-step-1">
        <div className="text-xs text-muted-foreground">الردود ستصل عملاءك باسم <b>{mb.name || mb.address}</b>، وأي رد منهم يعود إلى <span dir="ltr">{mb.address}</span>.</div>
      </Step>

      {mb.free_email ? (
        <Step tone="done" n="2" title="بريدك الجديد جاهز — شاركه مع عملائك" testId="mail-step-2">
          <div className="flex items-center gap-2 rounded-xl bg-background border border-border px-3 py-2 font-mono text-sm" dir="ltr">
            <span className="flex-1 truncate" data-testid="mail-free-address">{mb.address}</span>
            <button onClick={() => { navigator.clipboard.writeText(mb.address); toast.success("نُسخ"); }} className="w-8 h-8 rounded-full grid place-items-center hover:bg-secondary" aria-label="نسخ"><Copy className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-muted-foreground">ضعه في موقعك وحساباتك ووقّع به رسائلك. أي بريد يصل إليه يرد عليه المساعد باسم <b>{mb.name}</b> فورًا، وتصلك الحالات الحساسة في التصعيدات. لا يحتاج أي إعداد.</p>
        </Step>
      ) : (
      <Step tone={fwdTone} n="2" title={mb.forwarding_verified ? "إعادة التوجيه تعمل" : "المطلوب منك: وجّه بريدك إلى هذا العنوان"} testId="mail-step-2">
        <CopyField value={mb.receive_address} testId="mail-forward-address" />
        {!mb.forwarding_verified && (
          <ForwardingGuide address={mb.receive_address} client={mb.address} />
        )}
        <button onClick={verify} disabled={!!busy || waiting} className={`h-9 rounded-full px-4 text-xs font-bold flex items-center gap-1 disabled:opacity-50 ${mb.forwarding_verified ? "bg-secondary text-foreground" : "bg-red-500 text-white hover:bg-red-600"}`} data-testid="mail-verify-btn">
          {waiting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {waiting ? "بانتظار عودة الرسالة…" : mb.forwarding_verified ? "إعادة التحقق" : "أنجزت التوجيه — تحقق الآن"}
        </button>
        {mb.forwarding_verified && <div className="text-[11px] text-muted-foreground">آخر استقبال: {mb.forwarding_verified_at ? new Date(mb.forwarding_verified_at).toLocaleString("ar-SA-u-ca-gregory-nu-latn") : "—"}</div>}
      </Step>
      )}
      <Step tone={state === "paused" ? "paused" : mb.forwarding_verified ? "done" : "todo"} n="3" title={state === "paused" ? "الرد التلقائي متوقف" : "الرد التلقائي"} testId="mail-step-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => toggle(mb.enabled === false)} disabled={busy === "t"} className={`h-9 rounded-full px-4 text-xs font-bold flex items-center gap-1 ${mb.enabled === false ? "bg-emerald-500 text-white" : "bg-orange-500/15 text-orange-600 hover:bg-orange-500/25"}`} data-testid="mail-enabled-toggle">
            <Power className="w-3.5 h-3.5" /> {mb.enabled === false ? "تفعيل الرد" : "إيقاف الرد مؤقتًا"}
          </button>
          <span className="text-[11px] text-muted-foreground">{mb.enabled === false ? "الرسائل تُحفظ في الصندوق دون رد" : mb.forwarding_verified ? "البوت يرد تلقائيًا ويصعّد الحالات الحساسة" : "يبدأ الرد تلقائيًا بعد اكتمال الخطوة 2"}</span>
        </div>
      </Step>
    </div>
  );
};
